<template>
    <v-dialog
        v-model="showDialog"
        :width="displayWidth"
        persistent
        eager
        @keydown.esc="onEscapeKeydown"
    >
        <v-card ref="dialogCard" role="region" aria-label="user-details-dialog">
            <v-card-title class="px-4">
                <span>{{ dialogTitle }}</span>
                <help-button :href="helpUrl"></help-button>
                <span
                    v-show="formErrorMessage.length"
                    class="ml-5 text-body-1 error--text"
                    >{{ formErrorMessage }}</span
                >
            </v-card-title>
            <v-card-text class="px-5 py-0">
                <v-container fluid pa-0>
                    <v-row no-gutters>
                        <v-col>
                            <v-slide-y-reverse-transition
                                mode="in-out"
                                hide-on-leave
                            >
                                <div v-if="isExistingRecord && !isFormEditing">
                                    <user-read-form
                                        :value="userData"
                                        :allow-xoc-field="isXocUser"
                                    >
                                    </user-read-form>
                                </div>
                            </v-slide-y-reverse-transition>
                            <v-slide-y-transition mode="in-out" hide-on-leave>
                                <v-form
                                    v-if="isFormEditing || isNewRecord"
                                    ref="form"
                                    v-model="isFormValid"
                                >
                                    <user-edit-form
                                        v-model="userEditData"
                                        :allow-xoc-field="isXocUser"
                                        :xoc-auth="userEditData.xocAuth"
                                        :is-new-record="isNewRecord"
                                        :prefix="userEditData.prefix"
                                        :prefix-items="distinctMsos"
                                    ></user-edit-form>
                                </v-form>
                            </v-slide-y-transition>
                        </v-col>
                    </v-row>
                    <v-row no-gutters>
                        <v-col class="pa-0">
                            <!-- useScopes master/detail view -->
                            <component
                                :is="scopesViewComponent"
                                :allow-add="isFormEditing || isNewRecord"
                                :allow-edit="isFormEditing || isNewRecord"
                                :scopes-data="localUserScopes"
                                :grantable-scopes-data="userInfo.userScopes"
                                :assignable-scopes-data="userScopeOptions"
                                :xoc-auth="userEditData.xocAuth"
                                @change="onScopesChange"
                            />
                        </v-col>
                    </v-row>
                </v-container>
            </v-card-text>
            <v-card-actions class="pt-3">
                <v-col class="py-0">
                    <v-btn
                        v-if="showDeleteButton"
                        outlined
                        small
                        color="error"
                        :disabled="!allowDelete"
                        :loading="isDeleteLoading"
                        @click="onDeleteClick"
                        >Delete</v-btn
                    >
                </v-col>
                <v-col class="py-0 text-right">
                    <v-btn
                        v-if="showCancelButton"
                        small
                        outlined
                        class="mx-2"
                        @click="onCancelClick"
                        >Cancel</v-btn
                    >
                    <v-btn
                        v-if="showCloseButton"
                        small
                        outlined
                        class="mx-2"
                        @click="onCloseClick"
                        >Close</v-btn
                    >
                    <v-btn
                        v-if="showEditButton"
                        small
                        color="primary"
                        :disabled="!allowEdit"
                        @click="onEditClick"
                        >Edit</v-btn
                    >
                    <v-btn
                        v-if="showSaveButton"
                        small
                        color="primary"
                        :disabled="!allowSave"
                        :loading="isSaveLoading"
                        @click="onSaveClick"
                        >Save</v-btn
                    >
                </v-col>
            </v-card-actions>
        </v-card>
        <confirmation-dialog ref="confirmation"></confirmation-dialog>
    </v-dialog>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import UserReadForm from '../forms/UserReadForm';
import UserEditForm from '../forms/UserEditForm';
import ConfirmationDialog from '@/components/Dialogs/ConfirmationPromiseDialog';
import ScopeMasterDetailView from './ScopeMasterDetailView';
import SingleScopeView from './SingleScopeView';
import userScopeMixin from '@/mixins/userScopeMixin';
import HelpButton from '@/components/Buttons/HelpButton';

export default {
    name: 'UserDetailsDialog',
    components: {
        'user-read-form': UserReadForm,
        'user-edit-form': UserEditForm,
        'scope-master-detail-view': ScopeMasterDetailView,
        'single-scope-view': SingleScopeView,
        'confirmation-dialog': ConfirmationDialog,
        'help-button': HelpButton
    },
    mixins: [userScopeMixin],
    props: {
        showDialog: {
            type: Boolean,
            required: true,
            default: false
        },
        userId: {
            type: Number,
            required: true
        },
        profileType: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            isFormValid: true,
            isFormDirty: false,
            isFormEditing: false,
            isSaveLoading: false,
            isDeleteLoading: false,
            formErrorMessage: '',
            userEditData: {},
            // userScopes section variables
            scopesViewComponent: '',
            isScopesDirty: false,
            isScopesValid: false,
            localUserScopes: [],
            updatedUserScopes: []
        };
    },
    computed: {
        ...mapState('authUser', ['userInfo', 'userScopeOptions']),
        ...mapState('user', {
            userData: 'user',
            userScopes: 'userScopes'
        }),
        ...mapState('user', ['readHelpUrl', 'editHelpUrl', 'addHelpUrl']),
        ...mapGetters('authUser', ['isXocUser', 'distinctMsos']),
        ...mapGetters('venue', [
            'currentMso',
            'currentPartner',
            'currentVenue'
        ]),
        isNewRecord() {
            return this.userId <= 0;
        },
        isExistingRecord() {
            return this.userId > 0;
        },
        allowEdit() {
            return this.$can('edit', 'User');
        },
        allowSave() {
            return (
                (this.isFormDirty || this.isScopesDirty) &&
                this.isFormValid &&
                this.isScopesValid &&
                !this.isSaveLoading
            );
        },
        allowDelete() {
            return this.$can('delete', 'User') && !this.isDeleteLoading;
        },
        showDeleteButton() {
            return this.isExistingRecord && this.isFormEditing;
        },
        showCancelButton() {
            return (
                (this.isExistingRecord && this.isFormEditing) ||
                this.isNewRecord
            );
        },
        showCloseButton() {
            return this.isExistingRecord && !this.isFormEditing;
        },
        showEditButton() {
            return this.isExistingRecord && !this.isFormEditing;
        },
        showSaveButton() {
            return this.isFormEditing || this.isNewRecord;
        },
        dialogTitle() {
            if (this.isExistingRecord) {
                if (this.isFormEditing) {
                    return `Edit User: ${this.userData.firstName} ${this.userData.lastName}`;
                } else {
                    return `User: ${this.userData.firstName} ${this.userData.lastName}`;
                }
            } else {
                return `Add new user`;
            }
        },
        displayWidth() {
            if (this.$vuetify.breakpoint.mdAndUp) {
                if (this.isSingleScopeMode) {
                    return '800px';
                } else {
                    return '1100px';
                }
            }
            return '95%';
        },
        isSingleScopeMode() {
            return (
                this.userInfo &&
                this.userInfo.userScopes &&
                this.userInfo.userScopes.length === 1 &&
                !this.isScopeWildcardMatch(this.userInfo.userScopes[0])
            );
        },
        helpUrl() {
            if (this.isNewRecord) {
                return this.addHelpUrl;
            } else if (this.isFormEditing) {
                return this.editHelpUrl;
            }
            return this.readHelpUrl;
        }
    },
    watch: {
        userData(value) {
            this.userEditData = { ...value };
            setTimeout(() => {
                this.isFormDirty = false;
            }, 100);
        },
        userEditData() {
            this.isFormDirty = true;
            this.$nextTick(() => {
                this.checkUserScopes();
            });
        },
        userScopes() {
            this.$nextTick(() => {
                this.copyUserScopes();
            });
        }
    },
    async created() {
        this.fetchUserData();
    },
    methods: {
        ...mapActions('user', [
            'fetchUser',
            'resetUser',
            'createUser',
            'updateUserEntity',
            'deleteUser'
        ]),
        // ===================== Events =======================
        onCancelClick() {
            if (this.isFormDirty || this.isScopesDirty) {
                this.showSaveConfirmation();
            } else if (this.isExistingRecord) {
                this.resetUserForm();
            } else {
                this.closeThisDialog(false, 100);
            }
        },
        onEditClick() {
            this.isFormEditing = true;
            this.setEditFormFocus();
        },
        onCloseClick() {
            this.closeThisDialog(false);
        },
        onSaveClick() {
            this.saveThisUser();
        },
        onDeleteClick() {
            this.showDeleteConfirmation();
        },
        onScopesChange(data) {
            if (data) {
                this.isScopesDirty = data.isDirty;
                this.isScopesValid = data.isValid;
                this.updatedUserScopes = [...data.scopes];
                this.$nextTick(() => {
                    this.checkUserScopes();
                });
            }
        },
        onEscapeKeydown() {
            if (this.showCloseButton) {
                this.onCloseClick();
            } else if (this.showCancelButton) {
                this.onCancelClick();
            }
        },
        // ===================== Others =======================
        async fetchUserData() {
            if (this.isExistingRecord) {
                this.fetchUser({ userId: this.userId });
                if (this.isSingleScopeMode) {
                    this.scopesViewComponent = 'single-scope-view';
                } else {
                    this.scopesViewComponent = 'scope-master-detail-view';
                }
            } else if (this.isNewRecord) {
                this.userEditData = { prefix: this.currentMso.toLowerCase() };
                this.populateUserProfileScopes(
                    this.profileType,
                    this.isSingleScopeMode
                );
                if (this.isSingleScopeMode) {
                    this.scopesViewComponent = 'single-scope-view';
                } else {
                    this.scopesViewComponent = 'scope-master-detail-view';
                }
            }
        },
        copyUserScopes() {
            // copy userScopes to local
            this.localUserScopes = [];
            if (this.userInfo && this.userScopes && this.userScopes.length) {
                this.localUserScopes = this.transformUserScopesData(
                    this.userScopes,
                    this.userInfo.userScopes,
                    false
                );
            }
        },
        populateUserProfileScopes(profileType, isSingleScopeMode) {
            if (profileType === 'VENUE_USER') {
                const profileScopes = this.getDefaultUserProfileScope();
                this.localUserScopes = this.transformUserScopesData(
                    profileScopes,
                    this.userInfo.userScopes,
                    false
                );
            } else if (profileType === 'VENUE_ADMIN') {
                const profileScopes = this.getVenueAdminProfileScope();
                this.localUserScopes = this.transformUserScopesData(
                    profileScopes,
                    this.userInfo.userScopes,
                    false
                );
            } else if (isSingleScopeMode) {
                this.assignDefaultScope();
            }
        },
        getDefaultUserProfileScope() {
            const targetScope = {
                mso: this.currentMso,
                partner: this.currentPartner,
                venue: this.currentVenue
            };
            let userScopeRoles = this.getScopeRoles(targetScope, [
                { name: 'DEFAULT_USER', withGrant: false }
            ]);
            return [
                {
                    ...targetScope,
                    withGrant: false,
                    userScopeRoles
                }
            ];
        },
        getVenueAdminProfileScope() {
            const targetScope = {
                mso: this.currentMso,
                partner: this.currentPartner,
                venue: this.currentVenue
            };
            let userScopeRoles = this.getScopeRoles(targetScope, [
                { name: 'DEFAULT_USER', withGrant: true },
                { name: 'HOTEL_ADMIN', withGrant: true }
            ]);
            return [
                {
                    ...targetScope,
                    withGrant: true,
                    userScopeRoles
                }
            ];
        },
        getScopeRoles(targetScope, roles) {
            // will build scope roles for the given roles list
            let scopeRoles = [];
            roles.forEach(role => {
                const grantableRole = this.getGrantableRole(
                    targetScope,
                    role.name,
                    role.withGrant
                );
                if (grantableRole) {
                    scopeRoles.push(grantableRole);
                }
            });
            return scopeRoles;
        },
        getGrantableRole(targetScope, roleName, withGrant) {
            // will find the given role name in the grantable roles
            // and return role object to be added to user scopes
            const grantableRoles = this.getGrantableRoles(
                targetScope,
                this.userInfo.userScopes
            );
            if (grantableRoles && grantableRoles.length) {
                let role = grantableRoles.find(item => item.name === roleName);
                if (role) {
                    return {
                        roleId: role.id,
                        withGrant,
                        role
                    };
                }
            }
            return null;
        },
        assignDefaultScope() {
            // will assign the default scope from logged in user
            // to the current user
            if (
                this.userInfo &&
                this.userInfo.userScopes &&
                this.userInfo.userScopes.length
            ) {
                let defaultScopes = [];
                this.assignScope(
                    this.userInfo.userScopes[0],
                    defaultScopes,
                    this.userInfo.userScopes
                );
                this.localUserScopes = defaultScopes;
            }
        },
        closeThisDialog(refreshParentData = false, timeout = 500) {
            setTimeout(() => {
                this.resetDialogData();
                this.$emit('close', refreshParentData);
            }, timeout);
        },
        resetUserForm() {
            if (this.isFormDirty) {
                this.userEditData = { ...this.userData };
            }
            this.isFormValid = true;
            this.isFormEditing = false;
            this.isSaveLoading = false;
            this.isDeleteLoading = false;
            this.formErrorMessage = '';
            setTimeout(() => {
                this.isFormDirty = false;
            }, 100);
        },
        resetDialogData() {
            this.resetUserForm();
            this.scopesViewComponent = '';
            this.isScopesDirty = false;
            this.isScopesValid = false;
            this.updatedUserScopes = [];
            this.localUserScopes = [];
            this.resetUser();
            this.setReadFormFocus();
        },
        async showDeleteConfirmation() {
            const res = await this.$refs.confirmation.open(
                'Delete User',
                `Are you sure you want to delete this user: ${this.userData.firstName} ${this.userData.lastName}?`,
                '',
                {
                    confirmActionText: 'Delete user',
                    cancelActionText: 'Cancel',
                    showIcon: true
                }
            );
            if (res) {
                this.deleteThisUser();
            }
        },
        async showSaveConfirmation() {
            const res = await this.$refs.confirmation.open(
                'Edit User',
                'Are you sure you want to leave without saving?',
                'It look like you have some unsaved changes',
                {
                    confirmActionText: 'Save changes',
                    cancelActionText: 'Ignore change',
                    showIcon: true
                }
            );
            if (res) {
                this.saveThisUser();
            } else if (this.isNewRecord) {
                this.closeThisDialog();
            } else {
                this.resetUserForm();
                this.copyUserScopes();
            }
        },
        async deleteThisUser() {
            // will call the api method to delete the current user...
            try {
                this.isDeleteLoading = true;
                await this.deleteUser({
                    userId: this.userId
                });
                this.closeThisDialog(true, 1000);
            } finally {
                this.isDeleteLoading = false;
            }
        },
        async saveThisUser() {
            try {
                this.isSaveLoading = true;
                if (this.isFormDirty && this.isNewRecord) {
                    await this.createUser({
                        userInfo: this.userEditData,
                        userScopes: this.updatedUserScopes
                    });
                    this.closeThisDialog(true, 1000);
                } else if (
                    this.isExistingRecord &&
                    (this.isFormDirty || this.isScopesDirty)
                ) {
                    let diff = {};
                    if (this.isScopesDirty) {
                        diff = this.diffUserScopes(
                            this.updatedUserScopes,
                            this.userScopes
                        );
                    }
                    await this.updateUserEntity({
                        newUserInfo: this.userEditData,
                        oldUserInfo: this.userData,
                        userScopes: diff
                    });
                    this.closeThisDialog(true, 1000);
                }
            } catch (error) {
                console.log(error);
            } finally {
                this.isSaveLoading = false;
            }
        },
        setEditFormFocus() {
            this.$nextTick(() => {
                const form = this.$refs.form;
                if (form) {
                    form.$el
                        .querySelector('.v-input input:not([disabled])')
                        .focus();
                }
            });
        },
        setReadFormFocus() {
            this.$nextTick(() => {
                const dialogCard = this.$refs.dialogCard;
                if (dialogCard) {
                    dialogCard.$el
                        .querySelector(
                            '.v-btn:not([disabled]):not([style*="display: none"])'
                        )
                        .focus();
                }
            });
        },
        checkUserScopes() {
            if (this.hasWildcardScopes()) {
                this.isScopesValid = false;
                this.formErrorMessage =
                    'Cannot assign a wildcard scope to a non-mso user.';
            } else {
                this.isScopesValid = true;
                this.formErrorMessage = '';
            }
        },
        hasWildcardScopes() {
            if (!this.userEditData.xocAuth) {
                return (
                    this.updatedUserScopes.length &&
                    this.updatedUserScopes.find(
                        scope => scope.name.indexOf('*') >= 0
                    )
                );
            }
            return false;
        }
    }
};
</script>

<style lang="scss" scoped></style>
